import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { AnimatedSection } from '../components/AnimatedSection';
import { toast } from 'sonner';
import { CheckCircle, ArrowRight, ArrowLeft, Calendar, Users, DollarSign, FileText, User } from 'lucide-react';

const API = `${"https://api.imerorganizasyon.com"}/api`;

const eventTypes = [
  { id: 'corporate', label: 'Kurumsal Etkinlik', desc: 'Gala, zirve, lansman, basın' },
  { id: 'wedding', label: 'Düğün', desc: 'Nikan, düğün töreni, gece' },
  { id: 'launch', label: 'Açılış & Lansman', desc: 'Marka açılışı, ürün lansmanı' },
  { id: 'private', label: 'Özel Davet', desc: 'Doğum günü, yıl dönümü, kutlama' },
];

const guestCounts = [
  '1-50 Kişi', '51-150 Kişi', '151-300 Kişi', '301-500 Kişi', '500+ Kişi'
];

const budgetRanges = [
  '50.000 - 100.000 TL', '100.000 - 250.000 TL', '250.000 - 500.000 TL', '500.000 TL+', 'Görüşmeyi tercih ederim'
];

const steps = [
  { id: 1, icon: FileText, title: 'Etkinlik Türü' },
  { id: 2, icon: Calendar, title: 'Tarih & Şehir' },
  { id: 3, icon: Users, title: 'Kapsam' },
  { id: 4, icon: FileText, title: 'Bilgi Notu' },
  { id: 5, icon: User, title: 'İletişim' },
];

export default function Quote() {
  const [currentStep, setCurrentStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    event_type: '',
    event_date: '',
    city: '',
    venue: '',
    guest_count: '',
    budget_range: '',
    brief: '',
    name: '',
    phone: '',
    email: '',
  });

  const update = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const canAdvance = () => {
    if (currentStep === 1) return !!form.event_type;
    if (currentStep === 2) return !!form.city;
    if (currentStep === 3) return !!form.guest_count;
    if (currentStep === 4) return true;
    if (currentStep === 5) return !!form.name && !!form.phone && !!form.email;
    return false;
  };

  const handleSubmit = async () => {
    if (!canAdvance()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/quote`, form);
      setSubmitted(true);
      toast.success('Teklif talebiniz alındı!');
    } catch (err) {
      toast.error('Bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  const progress = ((currentStep - 1) / (steps.length - 1)) * 100;

  const stepVariants = {
    initial: (dir) => ({ opacity: 0, x: dir > 0 ? 40 : -40 }),
    animate: { opacity: 1, x: 0 },
    exit: (dir) => ({ opacity: 0, x: dir > 0 ? -40 : 40 }),
  };

  return (
    <div style={{ backgroundColor: '#070708' }}>
      {/* Hero */}
      <section
        className="flex items-end overflow-hidden"
        style={{ paddingBottom: '4rem', paddingTop: '9rem', backgroundColor: '#0B0B0D' }}
      >
        <div className="container-luxury">
          <motion.span
            className="overline mb-5 block"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            Teklif & Danışmanlık
          </motion.span>
          <motion.h1
            className="display-xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.85, ease: [0.22,1,0.36,1] }}
          >
            Teklif Al
          </motion.h1>
        </div>
      </section>

      <section className="section-py" style={{ paddingTop: '4rem' }}>
        <div className="container-luxury">
          <div className="max-w-2xl mx-auto">
            {submitted ? (
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center py-16 px-8"
                style={{ border: '1px solid rgba(201,168,76,0.15)', backgroundColor: '#0E0F12' }}
              >
                <CheckCircle size={56} className="text-[#C9A84C] mx-auto mb-6" />
                <h2 className="font-display text-4xl font-medium text-[#F5F0E8] mb-4">Talebiniz Alındı</h2>
                <p className="body-text mb-8">
                  Teklif talebiniz başarıyla iletildi. 24 saat içinde sizinle iletişime geçeceğiz.
                </p>
                <div className="flex justify-center gap-4 flex-wrap">
                  <button
                    onClick={() => { setSubmitted(false); setCurrentStep(1); setForm({ event_type: '', event_date: '', city: '', venue: '', guest_count: '', budget_range: '', brief: '', name: '', phone: '', email: '' }); }}
                    className="btn-outline-gold text-xs tracking-[0.12em] uppercase"
                  >
                    Yeni Teklif
                  </button>
                </div>
              </motion.div>
            ) : (
              <div
                data-testid="quote-wizard"
                style={{ border: '1px solid rgba(201,168,76,0.10)', backgroundColor: '#0E0F12' }}
              >
                {/* Progress bar */}
                <div className="px-8 pt-8 pb-6">
                  <div className="flex justify-between mb-3">
                    {steps.map((step) => (
                      <div
                        key={step.id}
                        className="flex flex-col items-center gap-1 flex-1"
                      >
                        <div
                          className="w-8 h-8 rounded-full flex items-center justify-center font-body text-xs font-medium transition-all duration-300"
                          style={{
                            backgroundColor: currentStep >= step.id ? '#C9A84C' : 'rgba(201,168,76,0.1)',
                            color: currentStep >= step.id ? '#070708' : 'rgba(245,240,232,0.4)',
                            border: `1px solid ${currentStep >= step.id ? '#C9A84C' : 'rgba(201,168,76,0.15)'}`,
                          }}
                        >
                          {currentStep > step.id ? <CheckCircle size={14} /> : step.id}
                        </div>
                        <span
                          className="font-body text-[0.6rem] tracking-wide text-center hidden sm:block"
                          style={{ color: currentStep >= step.id ? '#C9A84C' : 'rgba(245,240,232,0.35)' }}
                        >
                          {step.title}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="h-0.5 w-full" style={{ backgroundColor: 'rgba(201,168,76,0.1)' }}>
                    <motion.div
                      className="h-full"
                      style={{ backgroundColor: '#C9A84C' }}
                      animate={{ width: `${progress}%` }}
                      transition={{ duration: 0.4, ease: 'easeInOut' }}
                    />
                  </div>
                </div>

                {/* Step Content */}
                <div className="px-8 pb-8" style={{ minHeight: '360px' }}>
                  <AnimatePresence mode="wait" custom={1}>
                    {currentStep === 1 && (
                      <motion.div
                        key={1}
                        variants={stepVariants}
                        custom={1}
                        initial="initial"
                        animate="animate"
                        exit="exit"
                        transition={{ duration: 0.35, ease: [0.22,1,0.36,1] }}
                      >
                        <h3 className="font-display text-2xl text-[#F5F0E8] mb-2">Etkinlik Türü</h3>
                        <p className="body-text mb-7">Planladığınız etkinlik türünü seçin.</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {eventTypes.map(type => (
                            <button
                              key={type.id}
                              onClick={() => update('event_type', type.id)}
                              className="flex flex-col items-start p-5 text-left transition-all duration-200"
                              style={{
                                backgroundColor: form.event_type === type.id ? 'rgba(201,168,76,0.12)' : 'transparent',
                                border: `1px solid ${form.event_type === type.id ? '#C9A84C' : 'rgba(201,168,76,0.15)'}`,
                              }}
                            >
                              <span className="font-body text-sm font-medium text-[#F5F0E8] mb-1">{type.label}</span>
                              <span className="font-body text-xs text-[rgba(245,240,232,0.5)]">{type.desc}</span>
                            </button>
                          ))}
                        </div>
                      </motion.div>
                    )}

                    {currentStep === 2 && (
                      <motion.div
                        key={2}
                        variants={stepVariants}
                        custom={1}
                        initial="initial"
                        animate="animate"
                        exit="exit"
                        transition={{ duration: 0.35, ease: [0.22,1,0.36,1] }}
                      >
                        <h3 className="font-display text-2xl text-[#F5F0E8] mb-2">Tarih & Yer</h3>
                        <p className="body-text mb-7">Etkinlik tarihi ve şehri hakkında bilgi verin.</p>
                        <div className="flex flex-col gap-4">
                          <div className="flex flex-col gap-1.5">
                            <label className="font-body text-xs tracking-[0.1em] uppercase text-[rgba(245,240,232,0.45)]">Etkinlik Tarihi</label>
                            <input
                              type="date"
                              value={form.event_date}
                              onChange={e => update('event_date', e.target.value)}
                              className="w-full px-4 py-3 font-body text-sm text-[#F5F0E8] outline-none"
                              style={{ backgroundColor: 'rgba(201,168,76,0.05)', border: '1px solid rgba(201,168,76,0.2)', colorScheme: 'dark' }}
                            />
                          </div>
                          <div className="flex flex-col gap-1.5">
                            <label className="font-body text-xs tracking-[0.1em] uppercase text-[rgba(245,240,232,0.45)]">Şehir *</label>
                            <input
                              type="text"
                              value={form.city}
                              onChange={e => update('city', e.target.value)}
                              className="w-full px-4 py-3 font-body text-sm text-[#F5F0E8] outline-none"
                              style={{ backgroundColor: 'rgba(201,168,76,0.05)', border: '1px solid rgba(201,168,76,0.2)' }}
                              placeholder="Örn: Ankara, İstanbul..."
                            />
                          </div>
                          <div className="flex flex-col gap-1.5">
                            <label className="font-body text-xs tracking-[0.1em] uppercase text-[rgba(245,240,232,0.45)]">Mekan (opsiyonel)</label>
                            <input
                              type="text"
                              value={form.venue}
                              onChange={e => update('venue', e.target.value)}
                              className="w-full px-4 py-3 font-body text-sm text-[#F5F0E8] outline-none"
                              style={{ backgroundColor: 'rgba(201,168,76,0.05)', border: '1px solid rgba(201,168,76,0.2)' }}
                              placeholder="Mekan tercihiniz varsa belirtin"
                            />
                          </div>
                        </div>
                      </motion.div>
                    )}

                    {currentStep === 3 && (
                      <motion.div
                        key={3}
                        variants={stepVariants}
                        custom={1}
                        initial="initial"
                        animate="animate"
                        exit="exit"
                        transition={{ duration: 0.35, ease: [0.22,1,0.36,1] }}
                      >
                        <h3 className="font-display text-2xl text-[#F5F0E8] mb-2">Kapsam</h3>
                        <p className="body-text mb-7">Kişi sayısı ve bütçe aralığını belirtin.</p>
                        <div className="flex flex-col gap-6">
                          <div>
                            <label className="font-body text-xs tracking-[0.1em] uppercase text-[rgba(245,240,232,0.45)] block mb-3">Kişi Sayısı *</label>
                            <div className="flex flex-wrap gap-2">
                              {guestCounts.map(count => (
                                <button
                                  key={count}
                                  onClick={() => update('guest_count', count)}
                                  className="px-4 py-2 font-body text-xs transition-all duration-200"
                                  style={{
                                    backgroundColor: form.guest_count === count ? '#C9A84C' : 'transparent',
                                    color: form.guest_count === count ? '#070708' : 'rgba(245,240,232,0.65)',
                                    border: `1px solid ${form.guest_count === count ? '#C9A84C' : 'rgba(201,168,76,0.2)'}`,
                                  }}
                                >
                                  {count}
                                </button>
                              ))}
                            </div>
                          </div>
                          <div>
                            <label className="font-body text-xs tracking-[0.1em] uppercase text-[rgba(245,240,232,0.45)] block mb-3">Bütçe Aralığı</label>
                            <div className="flex flex-col gap-2">
                              {budgetRanges.map(range => (
                                <button
                                  key={range}
                                  onClick={() => update('budget_range', range)}
                                  className="px-4 py-3 font-body text-sm text-left transition-all duration-200"
                                  style={{
                                    backgroundColor: form.budget_range === range ? 'rgba(201,168,76,0.12)' : 'transparent',
                                    color: form.budget_range === range ? '#C9A84C' : 'rgba(245,240,232,0.65)',
                                    border: `1px solid ${form.budget_range === range ? '#C9A84C' : 'rgba(201,168,76,0.1)'}`,
                                  }}
                                >
                                  {range}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}

                    {currentStep === 4 && (
                      <motion.div
                        key={4}
                        variants={stepVariants}
                        custom={1}
                        initial="initial"
                        animate="animate"
                        exit="exit"
                        transition={{ duration: 0.35, ease: [0.22,1,0.36,1] }}
                      >
                        <h3 className="font-display text-2xl text-[#F5F0E8] mb-2">Bilgi Notu</h3>
                        <p className="body-text mb-7">Etkinliğiniz hakkında ek bilgi paylaşın (opsiyonel).</p>
                        <textarea
                          value={form.brief}
                          onChange={e => update('brief', e.target.value)}
                          rows={7}
                          className="w-full px-4 py-3 font-body text-sm text-[#F5F0E8] outline-none resize-none"
                          style={{ backgroundColor: 'rgba(201,168,76,0.05)', border: '1px solid rgba(201,168,76,0.2)' }}
                          placeholder="Etkinlik konseptiniz, beklentileriniz, özel istekleriniz..."
                        />
                      </motion.div>
                    )}

                    {currentStep === 5 && (
                      <motion.div
                        key={5}
                        variants={stepVariants}
                        custom={1}
                        initial="initial"
                        animate="animate"
                        exit="exit"
                        transition={{ duration: 0.35, ease: [0.22,1,0.36,1] }}
                      >
                        <h3 className="font-display text-2xl text-[#F5F0E8] mb-2">İletişim Bilgileri</h3>
                        <p className="body-text mb-7">Size en kısa sürede dönelım.</p>
                        <div className="flex flex-col gap-4">
                          <div className="flex flex-col gap-1.5">
                            <label className="font-body text-xs tracking-[0.1em] uppercase text-[rgba(245,240,232,0.45)]">Ad Soyad *</label>
                            <input
                              type="text"
                              value={form.name}
                              onChange={e => update('name', e.target.value)}
                              className="w-full px-4 py-3 font-body text-sm text-[#F5F0E8] outline-none"
                              style={{ backgroundColor: 'rgba(201,168,76,0.05)', border: '1px solid rgba(201,168,76,0.2)' }}
                              placeholder="Adınız"
                            />
                          </div>
                          <div className="flex flex-col gap-1.5">
                            <label className="font-body text-xs tracking-[0.1em] uppercase text-[rgba(245,240,232,0.45)]">Telefon *</label>
                            <input
                              type="tel"
                              value={form.phone}
                              onChange={e => update('phone', e.target.value)}
                              className="w-full px-4 py-3 font-body text-sm text-[#F5F0E8] outline-none"
                              style={{ backgroundColor: 'rgba(201,168,76,0.05)', border: '1px solid rgba(201,168,76,0.2)' }}
                              placeholder="+90 5xx xxx xx xx"
                            />
                          </div>
                          <div className="flex flex-col gap-1.5">
                            <label className="font-body text-xs tracking-[0.1em] uppercase text-[rgba(245,240,232,0.45)]">E-posta *</label>
                            <input
                              type="email"
                              value={form.email}
                              onChange={e => update('email', e.target.value)}
                              className="w-full px-4 py-3 font-body text-sm text-[#F5F0E8] outline-none"
                              style={{ backgroundColor: 'rgba(201,168,76,0.05)', border: '1px solid rgba(201,168,76,0.2)' }}
                              placeholder="eposta@adresiniz.com"
                            />
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Navigation */}
                  <div className="flex justify-between items-center mt-10 pt-6" style={{ borderTop: '1px solid rgba(201,168,76,0.08)' }}>
                    <button
                      data-testid="quote-back-button"
                      onClick={() => setCurrentStep(s => Math.max(1, s - 1))}
                      disabled={currentStep === 1}
                      className="flex items-center gap-2 font-body text-xs tracking-[0.1em] uppercase text-[rgba(245,240,232,0.5)] hover:text-[#F5F0E8] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                      <ArrowLeft size={14} /> Geri
                    </button>

                    {currentStep < 5 ? (
                      <button
                        data-testid="quote-next-button"
                        onClick={() => canAdvance() && setCurrentStep(s => s + 1)}
                        disabled={!canAdvance()}
                        className="btn-gold flex items-center gap-2 text-xs tracking-[0.12em] uppercase disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        İleri <ArrowRight size={14} />
                      </button>
                    ) : (
                      <button
                        data-testid="quote-submit-button"
                        onClick={handleSubmit}
                        disabled={!canAdvance() || loading}
                        className="btn-gold flex items-center gap-2 text-xs tracking-[0.12em] uppercase disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        {loading ? 'Gönderiliyor...' : <><CheckCircle size={14} /> Teklif Talep Et</>}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
